import {
  useCreateLayoutOptimizationMutation,
  useCreateStructuralEngineeringMutation,
  useFileUpload,
  useProjectId,
  useServiceName,
  useStartPipelineMutation,
  useCreateBuildingPackageMutation,
} from '@consigli/hooks';
import {
  LayoutType,
  PipelineName,
  StructuralType,
  UploadFile,
  JobType,
  BuildingPackageType,
  Language,
  BuildingPackageWorkflowStep,
} from '@consigli/types';
import { zip } from '@consigli/utils';
import limit from 'p-limit';
import { useCallback, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Route } from '@/routes';

import { PackageCreationHook } from './use-create-insight-package';

enum ReportType {
  ACOUSTIC_REPORT = 'ACOUSTIC_REPORT',
  FIRE_REPORT = 'FIRE_REPORT',
  COMMISSIONING_REPORT = 'COMMISSIONING_REPORT',
}

interface StructuralPayload {
  type: StructuralType;
  name: string;
  fileIds: string[];
  data: {
    comments: string;
  };
}

interface LayoutPayload {
  type: LayoutType;
  name: string;
  file_ids: string[];
  language?: Language;
  data: {
    comments: string;
    ceilingHeight: unknown;
    reportsToGenerate?: ReportType[];
  };
}

interface CeilingPayload {
  name: string;
  file_ids: string[];
}

type Payload = StructuralPayload | LayoutPayload | CeilingPayload;

export const useCreatePackageUtil = ({
  setCurrentCounter,
  setTargetCounter,
}: PackageCreationHook) => {
  const { upload } = useFileUpload();
  const projectId = useProjectId();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const [comments] = useState('');
  const [createStructuralEngineering] = useCreateStructuralEngineeringMutation();
  const [createLayoutOptimization] = useCreateLayoutOptimizationMutation();
  const [createBuildingPackage] = useCreateBuildingPackageMutation();
  const [startPipeline] = useStartPipelineMutation();

  const uploadWithCounter = useCallback(
    async (file: File) => {
      let blob;
      try {
        blob = await upload(file, projectId);
      } finally {
        setCurrentCounter((counter) => counter + 1);
        return blob;
      }
    },
    [projectId, upload, setCurrentCounter],
  );

  const uploadFiles = useCallback(
    async (files: File[]): Promise<UploadFile[]> => {
      setCurrentCounter(0);
      setTargetCounter(files.length);

      const queue = limit(100);
      const input = files.map((file) => queue(uploadWithCounter, file));

      const results = await Promise.allSettled(input);

      const uploadFiles = files.map((file) => file as UploadFile);

      return zip(uploadFiles, results).map(([file, promise]) => {
        if (promise.status === 'fulfilled' && promise.value) {
          file.id = promise.value.id;
        }
        return file;
      });
    },
    [uploadWithCounter, setTargetCounter, setCurrentCounter],
  );

  const onAnalyze = useCallback(
    async (
      localFiles: File[],
      formData: FieldValues,
      type: StructuralType | LayoutType | BuildingPackageType,
      workflowStep?: BuildingPackageWorkflowStep,
    ) => {
      try {
        const files = await uploadFiles(localFiles);
        const fileIds = files.map((file) => file.id).filter((id): id is string => id !== undefined);
        let payload: Payload;
        let response;
        if (type === StructuralType.STRUCTURAL) {
          payload = {
            name: formData['iterationName'],
            type,
            fileIds, // Use fileIds for StructuralType
            data: {
              comments,
            },
          };
          response = await createStructuralEngineering({
            projectId,
            payload,
          }).unwrap();
        } else if (
          type === BuildingPackageType.CEILING_GRID ||
          type === BuildingPackageType.LOAD_CALCULATION ||
          type === BuildingPackageType.WASTEWATER_TREATMENT
        ) {
          response = await createBuildingPackage({
            projectId,
            payload: {
              name: formData['iterationName'],
              file_ids: fileIds,
              type: type,
              language: formData['language'] as Language,
              workflow_step: workflowStep,
            },
          }).unwrap();
        } else {
          payload = {
            name: formData['iterationName'],
            type,
            file_ids: fileIds,
            language:
              formData['language'] && formData['language'] !== ''
                ? (formData['language'] as Language)
                : undefined,
            data: {
              comments: comments,
              ceilingHeight: formData['ceilingHeight'],
              reportsToGenerate: [
                formData['acousticReport'] && ReportType.ACOUSTIC_REPORT,
                formData['fireReport'] && ReportType.FIRE_REPORT,
                formData['commissioningReport'] && ReportType.COMMISSIONING_REPORT,
              ].filter(Boolean) as ReportType[],
            },
          };
          response = await createLayoutOptimization({
            projectId,
            payload,
          }).unwrap();
        }

        if (response) {
          toast.success(t('package-creation.analyze-started'));

          if (type === LayoutType.REPORTS) {
            await startPipeline({
              projectId,
              packageId: response.id,
              pipelineName: PipelineName.REPORT_GENERATION,
              jobType: JobType.LAYOUT_OPTIMIZATION,
              notify: true,
            });
          }
        }
        // Navigate based on package type
        if (type === BuildingPackageType.LOAD_CALCULATION) {
          navigate(
            `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${response.id}/${Route.PROCESSING_LOAD_CALCULATION}`,
          );
        } else if (type === BuildingPackageType.CEILING_GRID) {
          // No navigation for ceiling grid packages
        } else {
          // Navigate to dashboard for all other types (LayoutType)
          navigate(
            `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${response.id}/${Route.DASHBOARD}`,
          );
        }
        return response;
      } catch (error) {
        console.log('Error creating package:', error);
        toast.error(t(`package-creation.failed-analyzed`));
      }
    },
    [
      navigate,
      projectId,
      serviceName,
      t,
      uploadFiles,
      comments,
      createStructuralEngineering,
      createLayoutOptimization,
      createBuildingPackage,
      startPipeline,
    ],
  );

  const startAnalysis = useCallback(
    async (
      packageId: string,
      pipelineName: PipelineName,
      jobType: JobType = JobType.BUILDING_PACKAGE,
    ) => {
      await startPipeline({
        projectId,
        packageId,
        pipelineName,
        notify: true,
        jobType,
      });
    },
    [startPipeline, projectId],
  );

  return {
    uploadFiles,
    onAnalyze,
    startAnalysis,
  };
};
